// src/components/document/DocumentSplitDetail.tsx
// The split-view detail pane for the document workspace: a compact identity
// card for one document — number, status, type, owner, version, review date —
// with the lifecycle facts the list views can't show. "Open full" hands off
// to the real detail page.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Badge, Button } from '@react/components';
import { useDocument } from '@react/hooks';

import {
  getDocumentStatus,
  getDocumentType,
  getDocumentOwner,
  getStatusMapping,
  documentStatusMap,
  documentTypeMap,
  formatRelativeTime,
} from '@/features/document/logic';
import { EntityAvatar } from '@/components/EntityAvatar';
import { toMs } from '@/features/document/chronicleLogic';

interface DocumentSplitDetailProps {
  documentId: string;
  onOpen: (id: string) => void;
}

export const DocumentSplitDetail = observer(({ documentId, onOpen }: DocumentSplitDetailProps) => {
  const store = useDocument();
  const { t, locale } = useI18n();
  const [doc, setDoc] = useState<any>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDoc(store.getById(documentId) ?? null);
    setMissing(false);
    store.GetDocument(documentId)
      .then((item: any) => { if (!cancelled && item) setDoc(item); })
      .catch(() => { if (!cancelled) setMissing(true); });
    return () => { cancelled = true; };
  }, [documentId, store]);

  if (missing) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">{t('document.splitDetail.loadError')}</p>
        <Button size="sm" className="mt-3" onClick={() => onOpen(documentId)}>
          {t('document.openFull')}
        </Button>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="space-y-3 p-4">
        <div className="h-6 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
        <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  const status = getDocumentStatus(doc);
  const docType = getDocumentType(doc);
  const statusInfo = getStatusMapping(documentStatusMap, status);
  const typeInfo = getStatusMapping(documentTypeMap, docType);
  const owner = getDocumentOwner(doc);
  const reviewMs = toMs(doc.next_review_date);
  const hasReview = reviewMs > 0 && new Date(reviewMs).getFullYear() > 1971;

  const rows: Array<{ label: string; node: React.ReactNode }> = [
    {
      label: t('document.status'),
      node: <Badge variant={statusInfo.variant as any} size="sm">{t(statusInfo.labelKey)}</Badge>,
    },
    {
      label: t('document.docType'),
      node: <Badge variant={typeInfo.variant as any} size="sm">{t(typeInfo.labelKey)}</Badge>,
    },
    ...(doc.document_number
      ? [{ label: t('document.splitDetail.number'), node: <span className="text-sm tabular-nums text-foreground">{doc.document_number}</span> }]
      : []),
    ...(doc.version
      ? [{ label: t('document.splitDetail.version'), node: <span className="text-sm tabular-nums text-foreground">v{doc.version}</span> }]
      : []),
    ...(hasReview
      ? [{ label: t('document.splitDetail.review'), node: <span className="text-sm tabular-nums text-foreground">{formatRelativeTime(new Date(reviewMs).toISOString(), locale)}</span> }]
      : []),
  ];

  return (
    <div className="p-4">
      <div className="space-y-4">
        {/* Identity */}
        <div>
          <h3 className="text-base font-semibold leading-snug text-foreground">
            {doc.title || t('common.untitled')}
          </h3>
          <div className="mt-1.5 flex items-center gap-2">
            {owner ? (
              <>
                <EntityAvatar name={owner} image={doc.owner?.avatar} userId={doc.owner?.entity?.id} size="xs" className="h-5 w-5 text-[10px]" />
                <span className="text-xs text-muted-foreground">{owner}</span>
              </>
            ) : (
              doc.updated_at && (
                <span className="text-xs text-muted-foreground">
                  {formatRelativeTime(doc.updated_at, locale)}
                </span>
              )
            )}
            {owner && doc.updated_at && (
              <span className="text-xs text-muted-foreground">· {formatRelativeTime(doc.updated_at, locale)}</span>
            )}
          </div>
        </div>

        {/* Fact rows */}
        <dl className="space-y-2">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-3">
              <dt className="text-xs text-muted-foreground">{row.label}</dt>
              <dd>{row.node}</dd>
            </div>
          ))}
        </dl>

        {doc.description && (
          <p className="line-clamp-3 text-[13px] leading-relaxed text-muted-foreground">{doc.description}</p>
        )}

        <Button size="sm" onClick={() => onOpen(documentId)}>
          {t('document.openFull')}
        </Button>
      </div>
    </div>
  );
});
